/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import {
  updateDashboardSequenceV1API,
  updateDashboardEnabledStatusV1API,
  deleteDashboardV1API,
} from '../services/DashboardAPIs';

import * as SummaryAPI from 'SummaryAPI';

export const ADD_SUMMARY = 'ADD_SUMMARY';
export const ADD_SUMMARY_FAILURE = 'ADD_SUMMARY_FAILURE';
export const ADD_SUMMARY_SUCCESS = 'ADD_SUMMARY_SUCCESS';

export const GET_SUMMARIES = 'GET_SUMMARIES';
export const GET_SUMMARIES_FAILURE = 'GET_SUMMARIES_FAILURE';
export const GET_SUMMARIES_SUCCESS = 'GET_SUMMARIES_SUCCESS';

export const GET_SUMMARY = 'GET_SUMMARY';
export const GET_SUMMARY_FAILURE = 'GET_SUMMARY_FAILURE';
export const GET_SUMMARY_SUCCESS = 'GET_SUMMARY_SUCCESS';
export const CLEAR_SUMMARY = 'CLEAR_SUMMARY';

export const DELETE_SUMMARY = 'DELETE_SUMMARY';
export const DELETE_SUMMARY_FAILURE = 'DELETE_SUMMARY_FAILURE';
export const DELETE_SUMMARY_SUCCESS = 'DELETE_SUMMARY_SUCCESS';

export const UPDATE_SUMMARY = 'UPDATE_SUMMARY';
export const UPDATE_SUMMARY_FAILURE = 'UPDATE_SUMMARY_FAILURE';
export const UPDATE_SUMMARY_SUCCESS = 'UPDATE_SUMMARY_SUCCESS';

export const GET_VALID_DATAITEMS_SUMMARY = 'GET_VALID_DATAITEMS_SUMMARY';
export const GET_VALID_DATAITEMS_SUMMARY_FAILURE = 'GET_VALID_DATAITEMS_SUMMARY_FAILURE';
export const GET_VALID_DATAITEMS_SUMMARY_SUCCESS = 'GET_VALID_DATAITEMS_SUMMARY_SUCCESS';

export const CLEAR_VALID_DATAITEMS_SUMMARY = 'CLEAR_VALID_DATAITEMS_SUMMARY';

// ---------------
// Summaries
// ---------------
const getSummaries = () => ({
  type: GET_SUMMARIES,
});

const getSummariesFailure = error => ({
  type: GET_SUMMARIES_FAILURE,
  payload: error,
});

const getSummariesSuccess = granularities => ({
  type: GET_SUMMARIES_SUCCESS,
  payload: granularities,
});

export const fetchSummaries = entityType => dispatch => {
  dispatch(getSummaries());
  return SummaryAPI.getSummaries(entityType)
    .then(responseJson => {
      const mergedResult = Array.isArray(responseJson)
        ? responseJson.map(summaryConfig => ({
            ...summaryConfig,
          }))
        : responseJson;
      dispatch(getSummariesSuccess(mergedResult || []));
      return mergedResult;
    })
    .catch(error => {
      dispatch(getSummariesFailure(error));
    });
};

const getSummary = () => ({
  type: GET_SUMMARY,
});

const getSummaryFailure = error => ({
  type: GET_SUMMARY_FAILURE,
  payload: error,
});

const getSummarySuccess = granularities => ({
  type: GET_SUMMARY_SUCCESS,
  payload: granularities,
});

export const clearSummary = () => ({
  type: CLEAR_SUMMARY,
});

export const fetchSummary = (entityTypeName, dashboardMetaId) => dispatch => {
  dispatch(getSummary());
  return SummaryAPI.getSummary(entityTypeName, dashboardMetaId)
    .then(responseJson =>
      // Check to see if the summary is available on load
      SummaryAPI.getSummaryFilters(
        entityTypeName,
        responseJson?.summaryDto?.granularityTimeFrequency,
        responseJson?.summaryDto?.dimensionNames
      ).then(filterResponseJson => {
        const result = {
          ...responseJson,
          summaryFilters: filterResponseJson,
        };
        dispatch(getSummarySuccess(result || {}));
      })
    )
    .catch(error => {
      dispatch(getSummaryFailure(error));
    });
};

// ---------------
// Add granularity
// ---------------
const startAddSummary = () => ({
  type: ADD_SUMMARY,
});

const addSummaryFailure = error => ({
  type: ADD_SUMMARY_FAILURE,
  payload: error,
});

const addSummarySuccess = granularities => ({
  type: ADD_SUMMARY_SUCCESS,
  payload: granularities,
});

export const addSummary =
  (entityType, summary) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startAddSummary());
    const userEmailId = dataCache.get('currentUser')?.id;

    return SummaryAPI.addSummary(entityType, summary, userEmailId)
      .then(responseJson => {
        dispatch(addSummarySuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(addSummaryFailure(error));
        throw error;
      });
  };

// ---------------
// Update summary
// ---------------
const startUpdateSummary = () => ({
  type: UPDATE_SUMMARY,
});

const updateSummaryFailure = error => ({
  type: UPDATE_SUMMARY_FAILURE,
  payload: error,
});

const updateSummarySuccess = granularities => ({
  type: UPDATE_SUMMARY_SUCCESS,
  payload: granularities,
});

export const updateSummary =
  (entityType, summary) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startUpdateSummary());
    const userEmailId = dataCache.get('currentUser')?.id;

    return SummaryAPI.updateSummary(entityType, summary, userEmailId)
      .then(responseJson => {
        dispatch(updateSummarySuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(updateSummaryFailure(error));
        throw error;
      });
  };

/**
 * this is a special case of update that calls a different API in the backend
 * @param {*} dashboardMetaId the unique id of the summary dashboard
 * @param {number} sequenceNumber
 */
export const updateSummarySequence =
  (entityType, dashboardMetaId, sequenceNumber) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startUpdateSummary());
    const userEmailId = dataCache.get('currentUser')?.id;
    return updateDashboardSequenceV1API(
      entityType,
      null,
      dashboardMetaId,
      sequenceNumber,
      userEmailId
    )
      .then(() => {
        // Need to refetch all summaries because the sequence numbers might have changed
        fetchSummaries(entityType)(dispatch);
      })
      .catch(error => {
        dispatch(updateSummaryFailure(error));
        throw error;
      });
  };

/**
 * this is a special case of update that calls a different API in the backend to update the isEnabled bit
 * @param {*} dashboardMetaId the unique id of the summary dashboard
 * @param {bool} isEnabled
 */
export const updateSummaryEnabled =
  (entityType, dashboardMetaId, isEnabled) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startUpdateSummary());
    const userEmailId = dataCache.get('currentUser')?.id;
    return updateDashboardEnabledStatusV1API(
      entityType,
      null,
      dashboardMetaId,
      isEnabled,
      userEmailId
    )
      .then(responseJson => {
        dispatch(updateSummarySuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(updateSummaryFailure(error));
        throw error;
      });
  };

// ---------------
// Delete granularity
// ---------------
const startDeleteSummary = () => ({
  type: DELETE_SUMMARY,
});

const deleteSummaryFailure = error => ({
  type: DELETE_SUMMARY_FAILURE,
  payload: error,
});

const deleteSummarySuccess = (entityType, dashboardMetaId) => ({
  type: DELETE_SUMMARY_SUCCESS,
  payload: { entityType, dashboardMetaId },
});

export const deleteSummary =
  (entityType, dashboardMetaId) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startDeleteSummary());
    const userEmailId = dataCache.get('currentUser')?.id;
    return deleteDashboardV1API(entityType, null, dashboardMetaId, userEmailId)
      .then(responseJson => {
        dispatch(deleteSummarySuccess(entityType, dashboardMetaId));
        return responseJson;
      })
      .catch(error => {
        dispatch(deleteSummaryFailure(error));
        throw error;
      });
  };

// ---------------
// EntityType Summary Dashboard Data Items Actions
// ---------------

const getValidDataItemsForSummary = () => ({
  type: GET_VALID_DATAITEMS_SUMMARY,
});

const getValidDataItemsForSummaryFailure = error => ({
  type: GET_VALID_DATAITEMS_SUMMARY_FAILURE,
  payload: error,
});

const getValidDataItemsForSummarySuccess = dataItems => ({
  type: GET_VALID_DATAITEMS_SUMMARY_SUCCESS,
  payload: dataItems,
});

export const clearValidDataItemsForSummary = () => ({
  type: CLEAR_VALID_DATAITEMS_SUMMARY,
});

export const fetchValidDataItemsForSummary = (entityType, timeGrain, dimensions) => dispatch => {
  dispatch(getValidDataItemsForSummary());
  return SummaryAPI.getValidDataItemsForSummary(entityType, timeGrain, dimensions)
    .then(responseData => {
      dispatch(getValidDataItemsForSummarySuccess(responseData));
      return responseData;
    })
    .catch(error => {
      dispatch(getValidDataItemsForSummaryFailure(error));
    });
};
